import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Shield, Mail, Lock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Redirect if already logged in as admin
    if (user && (user.role === 'admin' || user.role === 'super_admin')) {
      navigate('/super-admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/admin/login`, {
        email,
        password
      });

      if (response.data.user.role !== 'admin' && response.data.user.role !== 'super_admin') {
        setError('Access denied. Admin privileges required.');
        return;
      }

      login(response.data.user, response.data.token);
      toast.success('Welcome back, Admin!');
      
      // Small delay to ensure state is saved
      setTimeout(() => {
        navigate('/super-admin');
      }, 100);
    } catch (err) {
      console.error('Admin login error:', err);
      setError(err.response?.data?.detail || 'Invalid credentials or insufficient permissions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 sm:p-8 rounded-2xl border bg-card/80 backdrop-blur-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-primary/10 border border-primary/20">
              <Shield className="text-primary" size={32} />
            </div>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground">
            Admin Portal
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            concurrenz.AI Management System
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="email"
                placeholder="admin@concurrenz.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
                data-testid="admin-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                required
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full rounded-full mt-6"
            disabled={loading}
            data-testid="admin-login-button"
          >
            {loading ? 'Signing in...' : 'Sign In to Admin'}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-border/50 text-center">
          <p className="text-xs text-muted-foreground">
            This portal is restricted to authorized administrators only.
          </p>
          <Button 
            variant="link" 
            size="sm" 
            onClick={() => navigate('/search')}
            className="mt-2 text-primary"
          >
            Return to Main Site
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
